import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { SharedLanguageService } from '@language';
import { RecordTypeStructure } from '../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import {
    GenericActionFailureToast,
    GenericActionSuccessToast,
    GenericActionToast,
    PartialToast,
    ShToastFactory,
    Toast
} from '../../../toasts/toast.module';
import { ViewerComponent } from '../../components/viewer/viewer.component';
import {
    GetExtensionWithMimeTypeFallback,
    IsDataTypeOriginalAccessibleInViewer
} from '../../helper/extension-to-viewer.helper';
import { ViewerSandboxService } from '../../sandbox/viewer-sandbox.service';

@Component({
    selector: 'sh-viewer-container',
    templateUrl: './viewer-container.component.html',
    styleUrls: ['./viewer-container.component.scss'],
    standalone: true,
    imports: [ViewerComponent, AsyncPipe]
})
export class ViewerContainerComponent implements OnInit {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    blobUrl$!: Observable<string | null>;
    originalSourceUrl$!: Observable<string | null>;
    extension$!: Observable<string | null>;

    toastId!: number;

    private mediaObjectId$: Observable<string | null>;
    private record$!: Observable<PartialRecord>;
    private sourceBlob$!: Observable<Blob | null>;

    constructor(
        private readonly sandbox: ViewerSandboxService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly toastFactory: ShToastFactory
    ) {}

    ngOnInit(): void {
        this.mediaObjectId$ = this.activatedRoute.paramMap.pipe(map(queryParamMap => queryParamMap.get('recordId')));

        this.initLoadingToast();

        this.initRecordStream();
        this.initOriginalSourceUrlStream();
        this.initSourceBlobStream();

        this.initTypeOfRepresentationStream();
        this.initUrlForBlobStream();
    }

    private initLoadingToast(): void {
        const viewToast: Toast = this.createViewToast();
        this.sandbox.addToast(viewToast);

        this.toastId = viewToast.id;
    }

    private initRecordStream(): void {
        this.record$ = this.mediaObjectId$.pipe(
            filter((id): id is string => id !== null),
            switchMap(id => this.sandbox.getRecord(id)),
            tap(record => this.registerConsultation(record)),
            switchMap(record =>
                this.getConsultationRecord(record).pipe(
                    shareReplay(1),
                    catchError(e => this.handleExportError(this.toastId, e))
                )
            ),
            shareReplay(1)
        );
    }

    private initOriginalSourceUrlStream(): void {
        // Will only be available on main Data record
        this.originalSourceUrl$ = this.record$.pipe(
            map(record => record.metadata.Internal?.PathToOriginal ?? null),
            shareReplay(1)
        );
    }

    private registerConsultation(record: PartialRecord): void {
        const isDataType = record.getRecordStructure() === RecordTypeStructure.Data;

        const recordId = isDataType ? record.getRecordId() : record.getParentRecordId();
        if (recordId) {
            this.sandbox.registerConsultation(recordId);
        }
    }

    // Get correct record to provide viewer with data
    private getConsultationRecord(record: PartialRecord): Observable<PartialRecord> {
        const isAccessRepresentation: boolean = record.metadata.Administrative?.IsAccess ?? false;
        const isOriginalRepresentation: boolean = record.metadata.Administrative?.IsOriginal ?? false;

        // Directly use access representation
        if (isAccessRepresentation) {
            return of(record);
        }

        // Get main record to use PathToOriginal
        if (isOriginalRepresentation) {
            const parentRecordId = record.getParentRecordId();
            return parentRecordId ? this.sandbox.getRecord(parentRecordId) : of(record);
        }

        // If original accessible for record, use that, else fetch Access Representation);
        return IsDataTypeOriginalAccessibleInViewer(record)
            ? of(record)
            : this.sandbox.getAccessRepresentation(record.getRecordId());
    }

    private initTypeOfRepresentationStream(): void {
        this.extension$ = this.record$.pipe(map(apiFragment => GetExtensionWithMimeTypeFallback(apiFragment)));
    }

    // #region Blob
    private initSourceBlobStream(): void {
        this.sourceBlob$ = this.record$.pipe(
            // Only fetch blob for access representations
            switchMap(record => (record.metadata.Administrative?.IsAccess ? this.getExportBlob(record) : of(null))),
            shareReplay(1),
            tap(() => this.handleExportSuccess(this.toastId)),
            catchError(e => this.handleExportError(this.toastId, e))
        );
    }

    private initUrlForBlobStream(): void {
        this.blobUrl$ = this.sourceBlob$.pipe(
            filter((blob): blob is Blob => blob !== null),
            map(blob => URL.createObjectURL(blob))
        );
    }

    private getExportBlob(record: PartialRecord): Observable<Blob> {
        return this.sandbox.getExportBlob(record).pipe(shareReplay(1));
    }

    private handleExportSuccess(toastId: number): void {
        this.sandbox.updateToast(toastId, this.createViewSuccessToast());
    }

    private handleExportError(toastId: number, e: any): Observable<never> {
        console.error(e);
        this.sandbox.updateToast(toastId, this.createViewErrorToast());
        return EMPTY;
    }
    // #endregion

    // #region Toasts
    private createViewToast(): Toast {
        return GenericActionToast(this.toastFactory, this.translateService.instant('viewer.toasts.busy'));
    }

    private createViewSuccessToast(): PartialToast {
        return GenericActionSuccessToast(this.translateService.instant('viewer.toasts.success'));
    }

    private createViewErrorToast(): PartialToast {
        return GenericActionFailureToast(this.translateService.instant('viewer.toasts.error'));
    }
    // #endregion
}
